import React, { useEffect, useState } from "react";
import { Row, Col, Button, Container, Card } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getReminderPreferencesByPracticeNumbers } from "../../acommsapi";
import SmsModal from "./AddEditSMSComms"; // Import SmsModal directly, not AddSms
import { getUserPracticeById } from "../../api";

// --- Add this helper near the top of the file (outside the component) ---
function fillTemplate(template, appt = {}, practice = {}) {
  if (!template) return "";

  const get = (obj, path, def = "") =>
    path.split(".").reduce((acc, k) => (acc && acc[k] != null ? acc[k] : undefined), obj) ?? def;

  const [datePart = "", timePart = ""] = String(appt.start || "").split("T");

  // Known placeholders (extend as needed)
  const map = {
    "appointment.patientFirstName": get(appt, "patientFirstName", ""),
    "appointment.patientLastName": get(appt, "patientLastName", ""),
    "appointment.date": datePart,
    "appointment.time": timePart,
    "appointment.resourceName": get(appt, "resourceName", ""),
    "appointment.practiceName": get(appt, "practiceName", get(practice, "practiceName", "")),

    // NEW location placeholders
    "appointment.location.name": get(appt, "location.name", ""),
    "appointment.location.address1": get(appt, "location.address1", ""),
    "appointment.location.address2": get(appt, "location.address2", ""),
    "appointment.location.city": get(appt, "location.city", ""),
    "appointment.location.province": get(appt, "location.province", ""),
    "appointment.location.postalCode": get(appt, "location.postalCode", ""),
  };

  return Object.entries(map).reduce(
    (txt, [key, val]) => txt.replace(new RegExp(`\\$\\{${key}\\}`, "g"), val),
    template
  );
}


export function SmsReminders({ refresh, onSmsUpdate = () => {} }) {
  const [smsData, setSmsData] = useState([]); // Array containing at most one entry
  const [selectedSms, setSelectedSms] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [practices, setPractices] = useState([]); // Array of { practiceNumber, practiceName }

// A sample appointment object for previewing (replace with a real appt if desired)
const sampleAppt = {
  patientFirstName: "Alex",
  patientLastName: "Smith",
  start: "2025-10-12T13:00", // naive string like your DB
  resourceName: "Dr. Taylor",
  practiceName: "Downtown Clinic",
  location: {
    name: "Main Location",
    address1: "123 King St",
    address2: "Suite 400",
    city: "Toronto",
    province: "ON",
    postalCode: "M5H 2Y2",
  },
};




  // Fetch SMS preferences and user practices
  const loadSmsData = async () => {
    const token = sessionStorage.getItem("user");
    if (!token) return;
    const decoded = jwtDecode(token);
    const practiceNumbers = decoded.practice || [];

    // Load user practice objects for mapping numbers → names
    const userPractices = await getUserPracticeById(decoded._id);
    setPractices(userPractices);

    // Load SMS preferences
    const result = await getReminderPreferencesByPracticeNumbers(practiceNumbers, "SMS");
    setSmsData(Array.isArray(result.data) ? result.data : []);
  };

  useEffect(() => {
    loadSmsData();
  }, [refresh]);

  const handleButtonClick = () => {
    if (smsData.length > 0) {
      setSelectedSms(smsData[0]);
    } else {
      setSelectedSms(null);
    }
    setModalShow(true);
  };

  // Helper: map array of practiceNumbers → array of practiceNames
  const mapToNames = (numbersArray) => {
    if (!Array.isArray(numbersArray)) return [];
    return numbersArray
      .map((num) => {
        const p = practices.find((pr) => pr.practiceNumber === num);
        return p ? p.practiceName : num;
      })
      .filter((name) => !!name);
  };

  // Helper: split names array into two roughly equal columns
  const splitInTwoCols = (names) => {
    const halfway = Math.ceil(names.length / 2);
    return [names.slice(0, halfway), names.slice(halfway)];
  };

  return (
    <Container fluid className="border-0">
      {smsData.length === 0 ? (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="text-primary mb-0">SMS Reminders</h5>
              <Button variant="primary" onClick={handleButtonClick}>
                Add SMS Reminder
              </Button>
            </div>
            <p className="text-muted">No SMS reminders configured.</p>
          </Card.Body>
        </Card>
      ) : (
        smsData.map((entry, idx) => {
          const oneDayNames = mapToNames(entry["1DayPracs"]);
          const sundayNames = mapToNames(entry["sundayPracs"]);
          const [oneDayCol1, oneDayCol2] = splitInTwoCols(oneDayNames);
          const [sundayCol1, sundayCol2] = splitInTwoCols(sundayNames);

           const previewPractice =
    practices.find(
      (p) => oneDayNames.includes(p.practiceName) || sundayNames.includes(p.practiceName)
    ) || practices[0] || {};

          return (
            <Card key={idx} className="mb-4 border-0 shadow-sm">
              <Card.Body>
                {/* Header with title + button */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="text-primary mb-0">SMS Reminders</h5>
                  <Button variant="primary" onClick={handleButtonClick}>
                    Edit Reminder
                  </Button>
                </div>

                {/* Two columns side by side with vertical divider */}
                <Row>
                  {/* 1 Day Before */}
                  <Col md={6}>
                    <h6 className="text-primary mb-3">1 Day Before</h6>
                    <Row>
                      <Col xs={6}>
                        {oneDayCol1.map((name, i) => (
                          <div key={i} className="mb-1">
                            {name}
                          </div>
                        ))}
                      </Col>
                      <Col xs={6}>
                        {oneDayCol2.map((name, i) => (
                          <div key={i} className="mb-1">
                            {name}
                          </div>
                        ))}
                      </Col>
                    </Row>
                  </Col>

                  {/* Divider Line */}
                  <Col
                    md={6}
                    className="border-start ps-4"
                  >
                    <h6 className="text-primary mb-3">On Sunday for the Week</h6>
                    <Row>
                      <Col xs={6}>
                        {sundayCol1.map((name, i) => (
                          <div key={i} className="mb-1">
                            {name}
                          </div>
                        ))}
                      </Col>
                      <Col xs={6}>
                        {sundayCol2.map((name, i) => (
                          <div key={i} className="mb-1">
                            {name}
                          </div>
                        ))}
                      </Col>
                    </Row>
                  </Col>
                </Row>

                {/* Messages in one row, side by side */}
                <Row className="mt-4">
  <Col md={6}>
    <strong>Message:</strong>
    <div className="border p-3 rounded bg-light mt-1">
      {entry["1DayText"] || "—"}
    </div>
    <div className="mt-2">
      <small className="text-muted d-block">Preview</small>
      <div className="border p-3 rounded mt-1">
        {fillTemplate(entry["1DayText"] || "", sampleAppt, previewPractice) || "—"}
      </div>
    </div>
  </Col>

  <Col md={6}>
    <strong>Message:</strong>
    <div className="border p-3 rounded bg-light mt-1">
      {entry["sundayText"] || "—"}
    </div>
    <div className="mt-2">
      <small className="text-muted d-block">Preview</small>
      <div className="border p-3 rounded mt-1">
        {fillTemplate(entry["sundayText"] || "", sampleAppt, previewPractice) || "—"}
      </div>
    </div>
  </Col>
</Row>

              </Card.Body>
            </Card>
          );
        })
      )}

      {/* Use SmsModal directly instead of AddSms */}
      <SmsModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        smsDetail={selectedSms}
        onSmsUpdate={() => {
          setModalShow(false);
          setSelectedSms(null);
          loadSmsData();
          onSmsUpdate();
        }}
      />
    </Container>
  );
}